import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsersQuery, type UserResponse } from '@/features/user';
import { useAuthQuery } from '@/features/auth/api/auth.queries';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { AddUserModal } from './AddUserModal';
import { DeleteUserConfirmModal } from './DeleteUserConfirmModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function UserManagementTabContent() {
  const navigate = useNavigate();
  const { userProfile } = useAuthQuery();
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);

  const { data, isLoading, isError } = useUsersQuery({
    page: 0,
    size: 20,
    sort: ['fullname,asc'],
  });

  const users = useMemo(() => data?.content ?? [], [data]);

  const handleEdit = (user: UserResponse) => {
    // If editing own account, redirect to profile tab
    if (user.userId === userProfile?.userId) {
      navigate('/settings?tab=profile');
      return;
    }
    // Otherwise, open edit modal
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleDelete = (user: UserResponse) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-slate-400">Loading users...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-rose-400">
          Failed to load users. Please try again later.
        </p>
      </div>
    );
  }

  if (!users.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-slate-400">No users found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-50">Users</h3>
          <p className="text-sm text-slate-400">
            List of users registered in the application.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAddUserModalOpen(true)}
        >
          <Plus className="w-4 h-4" />
          Add User
        </Button>
      </div>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        mode="add"
      />

      <AddUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setSelectedUser(null);
        }}
        mode="edit"
        user={selectedUser}
      />

      <DeleteUserConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      <div className="rounded-lg border border-slate-700 bg-slate-900/60">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700 hover:bg-transparent">
              <TableHead className="bg-slate-900/80 px-4 py-2 text-left font-semibold text-slate-300">
                NRP
              </TableHead>
              <TableHead className="bg-slate-900/80 px-4 py-2 text-left font-semibold text-slate-300">
                Fullname
              </TableHead>
              <TableHead className="bg-slate-900/80 px-4 py-2 text-left font-semibold text-slate-300">
                Role
              </TableHead>
              <TableHead className="bg-slate-900/80 px-4 py-2 text-left font-semibold text-slate-300">
                Created At
              </TableHead>
              <TableHead className="bg-slate-900/80 px-4 py-2 text-left font-semibold text-slate-300">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const isOwnAccount = user.userId === userProfile?.userId;
              return (
                <TableRow
                  key={user.userId}
                  className={cn(
                    'border-slate-800/80 hover:bg-slate-800/60 transition-colors'
                  )}
                >
                  <TableCell className="px-4 py-2 text-slate-200">
                    {user.nrp}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-slate-200">
                    {user.fullname}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-slate-300">
                    {user.role}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-slate-400 text-xs">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-400 hover:text-blue-300 transition-colors p-1"
                        title={isOwnAccount ? 'Edit Profile' : 'Edit User'}
                        aria-label={isOwnAccount ? 'Edit Profile' : 'Edit User'}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {!isOwnAccount && (
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-red-400 hover:text-red-300 transition-colors p-1"
                          title="Delete User"
                          aria-label="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
